import * as Haptics from 'expo-haptics';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withSequence,
  withSpring,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Path, Rect, Circle } from 'react-native-svg';

type Props = {
  visible: boolean;
  onDone: () => void;
};

function triggerHaptic() {
  void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
}

function triggerHeavyHaptic() {
  void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Heavy);
}

export function MoneyHandoffAnimation({ visible, onDone }: Props) {
  const { t } = useTranslation();
  const overlayOpacity  = useSharedValue(0);
  const noteX           = useSharedValue(0);     // banknote: 0 = inside wallet, 1 = at hand
  const noteOpacity     = useSharedValue(1);
  const handScale       = useSharedValue(0.8);
  const handOpacity     = useSharedValue(0);
  const catchScale      = useSharedValue(1);
  const checkOpacity    = useSharedValue(0);
  const checkScale      = useSharedValue(0);

  useEffect(() => {
    if (!visible) return;

    // Reset
    overlayOpacity.value = 0;
    noteX.value          = 0;
    noteOpacity.value    = 1;
    handScale.value      = 0.8;
    handOpacity.value    = 0;
    catchScale.value     = 1;
    checkOpacity.value   = 0;
    checkScale.value     = 0;

    const easeIO = Easing.inOut(Easing.quad);

    // Fade in overlay
    overlayOpacity.value = withTiming(1, { duration: 200 });

    // Phase 1 (0-700ms): banknote slides right out of wallet
    noteX.value = withTiming(1, { duration: 700, easing: easeIO });

    // Phase 2 (600ms): hand appears
    handOpacity.value = withDelay(600, withTiming(1, { duration: 200 }));

    // Phase 2 (700ms): hand catches with spring bounce + haptic
    const hapticTimer = setTimeout(() => runOnJS(triggerHaptic)(), 700);
    catchScale.value = withDelay(700,
      withSequence(
        withSpring(1.15, { damping: 5, stiffness: 280 }),
        withSpring(1.0,  { damping: 12, stiffness: 320 }),
      ),
    );

    // Phase 3 (1100ms): checkmark appears + heavy haptic
    const haptic2Timer = setTimeout(() => runOnJS(triggerHeavyHaptic)(), 1100);
    checkOpacity.value = withDelay(1100, withTiming(1, { duration: 200 }));
    checkScale.value   = withDelay(1100,
      withSequence(
        withSpring(1.2, { damping: 6, stiffness: 240 }),
        withSpring(1.0, { damping: 14, stiffness: 300 }),
      ),
    );

    // Fade out at 2100ms
    const fadeTimer = setTimeout(() => {
      overlayOpacity.value = withTiming(0, { duration: 350 }, (finished) => {
        if (finished) runOnJS(onDone)();
      });
    }, 2100);

    return () => {
      clearTimeout(hapticTimer);
      clearTimeout(haptic2Timer);
      clearTimeout(fadeTimer);
    };
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  const overlayStyle = useAnimatedStyle(() => ({ opacity: overlayOpacity.value }));

  const noteStyle = useAnimatedStyle(() => ({
    opacity: noteOpacity.value,
    transform: [{ translateX: noteX.value * 110 }],
  }));

  const handStyle = useAnimatedStyle(() => ({
    opacity: handOpacity.value,
    transform: [{ scale: handScale.value * catchScale.value }],
  }));

  const checkStyle = useAnimatedStyle(() => ({
    opacity: checkOpacity.value,
    transform: [{ scale: checkScale.value }],
  }));

  if (!visible) return null;

  return (
    <Animated.View style={[styles.overlay, overlayStyle]} pointerEvents="none">
      <View style={styles.scene}>

        {/* Wallet (fixed, left side) */}
        <View style={styles.walletWrap}>
          <Svg width={56} height={44} viewBox="0 0 56 44">
            {/* wallet body */}
            <Rect x="2" y="10" width="52" height="32" rx="5" fill="#C9A843" opacity={0.25} stroke="#C9A843" strokeWidth="1.5" />
            {/* wallet flap */}
            <Rect x="2" y="2" width="52" height="14" rx="4" fill="#C9A843" opacity={0.5} />
            {/* coin pocket */}
            <Circle cx="40" cy="28" r="7" fill="none" stroke="#C9A843" strokeWidth="1.5" opacity={0.7} />
            <Circle cx="40" cy="28" r="3" fill="#C9A843" opacity={0.5} />
          </Svg>
        </View>

        {/* Banknote (animates out of wallet) */}
        <Animated.View style={[styles.noteWrap, noteStyle]}>
          <Svg width={80} height={38} viewBox="0 0 80 38">
            {/* note body */}
            <Rect x="1" y="1" width="78" height="36" rx="4" fill="#1A2F45" stroke="#C9A843" strokeWidth="1.5" />
            {/* decoration stripe */}
            <Rect x="8" y="8" width="20" height="22" rx="3" fill="#C9A843" opacity={0.2} />
            {/* center value */}
            <Rect x="35" y="14" width="30" height="10" rx="2" fill="#C9A843" opacity={0.15} />
            {/* euro-ish symbol */}
            <Path d="M16 22 C16 17 22 15 22 15 M13 20 H19" stroke="#C9A843" strokeWidth="1.5" strokeLinecap="round" fill="none" />
          </Svg>
        </Animated.View>

        {/* Hand (catches note, right side) */}
        <Animated.View style={[styles.handWrap, handStyle]}>
          <Svg width={56} height={68} viewBox="0 0 56 68">
            {/* palm */}
            <Path
              d="M8 50 C8 60 12 68 28 68 C44 68 48 60 48 50 L48 28 C48 24 44 20 40 20 L40 12 C40 8 36 4 32 4 C28 4 24 8 24 12 L24 20 C20 20 16 24 16 28"
              fill="#C9A843"
              opacity={0.3}
              stroke="#C9A843"
              strokeWidth="1.5"
            />
            {/* fingers hint */}
            <Path d="M16 28 L16 20 C16 16 20 12 24 12" stroke="#C9A843" strokeWidth="1.2" strokeLinecap="round" fill="none" opacity={0.5} />
          </Svg>
        </Animated.View>

      </View>

      {/* Checkmark (phase 3) */}
      <Animated.View style={[styles.checkWrap, checkStyle]}>
        <Text style={styles.checkText}>✓</Text>
        <Text style={styles.checkLabel}>{t('common.settlement')}</Text>
      </Animated.View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(13,27,42,0.92)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999,
    gap: 36,
  },
  scene: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: 280,
    height: 80,
    position: 'relative',
  },
  walletWrap: {
    position: 'absolute',
    left: 20,
    top: 18,
  },
  noteWrap: {
    position: 'absolute',
    left: 30,
    top: 21,
  },
  handWrap: {
    position: 'absolute',
    right: 10,
    top: 6,
  },
  checkWrap: {
    alignItems: 'center',
    gap: 4,
  },
  checkText: {
    color: '#C9A843',
    fontSize: 48,
    fontWeight: '800',
    lineHeight: 54,
  },
  checkLabel: {
    color: '#C9A843',
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: 3,
  },
});
